/**
 * Accessibility: the checker's findings with their fixes, the reading
 * order, alternative text for figures, and descriptions for form fields.
 */

import { useEffect, useRef, useState } from 'react'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { api, type AccessCheck, type AccessReport, type FormField, type SessionInfo, type StructureBlock } from '../api'

interface Props {
  sessionId: string
  doc: PDFDocumentProxy
  fields: FormField[]
  selected: string | null
  onSelect: (block: StructureBlock | null) => void
  onBlocks: (blocks: StructureBlock[]) => void
  onChanged: (session: SessionInfo) => Promise<void>
  onOcr: (pages: number[]) => void
  onBookmarks: (blocks: StructureBlock[]) => void
  onGoTo: (pageIndex: number) => void
  onClose: () => void
}

type Tab = 'check' | 'order' | 'alt' | 'fields'

const ICON: Record<AccessCheck['status'], string> = { passed: '✔', failed: '✖', warning: '⚠', manual: '✋' }

const humanise = (name: string) =>
  name.replace(/[_.]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+\d+$/, '').trim()

export function AccessibilityPanel(props: Props) {
  const { sessionId } = props
  const [tab, setTab] = useState<Tab>('check')
  const [report, setReport] = useState<AccessReport | null>(null)
  const [blocks, setBlocks] = useState<StructureBlock[]>([])
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [title, setTitle] = useState('')
  const [language, setLanguage] = useState('en-CA')

  const load = async () => {
    setError('')
    try {
      const r = await api.accessibility(sessionId)
      setReport(r)
      setTitle(r.title)
      setLanguage(r.language || 'en-CA')
      const s = await api.structure(sessionId)
      setBlocks(s.blocks)
      props.onBlocks(s.blocks)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load() }, [sessionId])

  const act = async (label: string, work: () => Promise<{ session: SessionInfo }>) => {
    setBusy(label)
    setError('')
    try {
      const r = await work()
      await props.onChanged(r.session)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy('')
    }
  }

  const fix = (check: AccessCheck) => {
    switch (check.fix) {
      case 'autotag': return void act('Adding tags', () => api.autotag(sessionId, language))
      case 'tabs': return void act('Fixing tab order', () => api.fixTabOrder(sessionId))
      case 'properties': return void act('Saving', () => api.setAccessProperties(sessionId, { title, language }))
      case 'ocr': return props.onOcr(check.pages.map((p) => p - 1))
      case 'alt': return setTab('alt')
      case 'tooltips': return setTab('fields')
      case 'bookmarks': return props.onBookmarks(blocks)
    }
  }

  const failed = report?.checks.filter((c) => c.status === 'failed').length ?? 0
  return (
    <aside className="comments access" aria-label="Accessibility">
      <div className="comments__header">
        <span>Accessibility</span>
        <button className="iconbtn" title="Close" onClick={props.onClose}>{'✕'}</button>
      </div>
      <div className="access__tabs" role="tablist">
        {([['check', 'Check'], ['order', 'Reading order'], ['alt', 'Alt text'], ['fields', 'Fields']] as const).map(([id, label]) => (
          <button key={id} role="tab" aria-selected={tab === id} className="sidetab" onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      <div className="access__body">
        {error && <p className="modal__hint modal__hint--warn">{error}</p>}
        {busy && <p className="modal__hint">{busy}&hellip;</p>}

        {tab === 'check' && report && (
          <>
            <p className="access__summary">
              {failed ? `${failed} problem${failed === 1 ? '' : 's'} to fix.` : 'No problems a program can find.'}{' '}
              <button className="linkbtn" onClick={() => void load()}>Check again</button>
            </p>
            <div className="field-row">
              <label className="field"><span>Title</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What the document is" /></label>
              <label className="field"><span>Language</span>
                <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="en-CA">English (Canada)</option>
                  <option value="fr-CA">French (Canada)</option>
                  <option value="en-US">English (US)</option>
                </select></label>
            </div>
            <ul className="access__checks">
              {report.checks.map((check) => (
                <li key={check.id} className="access__check" data-status={check.status}>
                  <span className="access__icon" aria-label={check.status}>{ICON[check.status]}</span>
                  <div>
                    <strong>{check.title}</strong>
                    {check.detail && <div className="access__detail">{check.detail}</div>}
                    {check.pages.length > 0 && (
                      <div className="access__detail">
                        Pages: {check.pages.slice(0, 12).map((p) => (
                          <button key={p} className="linkbtn" onClick={() => props.onGoTo(p - 1)}>{p}</button>
                        ))}{check.pages.length > 12 ? '…' : ''}
                      </div>
                    )}
                    {check.fix && check.status !== 'passed' && (
                      <button className="btn btn--small" disabled={Boolean(busy)} onClick={() => fix(check)}>
                        {{ autotag: 'Add tags', tabs: 'Fix tab order', properties: 'Set title and language', ocr: 'Run OCR',
                          alt: 'Write alt text', tooltips: 'Describe fields', bookmarks: 'Bookmarks from headings' }[check.fix]}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {tab === 'order' && (
          <ReadingOrder blocks={blocks} selected={props.selected} tagged={report?.tagged ?? false}
                        onSelect={props.onSelect}
                        onType={(b, type) => void act('Saving', () => api.changeBlock(sessionId, b.id, { type }))}
                        onMove={(b, delta) => void act('Moving', async () => {
                          const r = await api.moveBlock(sessionId, b.id, delta)
                          props.onSelect(r.blocks.find((x) => x.id === r.id) ?? null)
                          return r
                        })}
                        onArtifact={(b) => void act('Saving', () => api.blockToArtifact(sessionId, b.id))} />
        )}

        {tab === 'alt' && (
          <AltText doc={props.doc} blocks={blocks.filter((b) => b.type === 'Figure')}
                   onSave={(b, alt) => void act('Saving', () => api.changeBlock(sessionId, b.id, { alt }))} />
        )}

        {tab === 'fields' && (
          <FieldTips fields={props.fields}
                     onSave={(values) => void act('Saving', () => api.setTooltips(sessionId, values))} />
        )}
      </div>
    </aside>
  )
}

function ReadingOrder({ blocks, selected, tagged, onSelect, onType, onMove, onArtifact }: {
  blocks: StructureBlock[]
  selected: string | null
  tagged: boolean
  onSelect: (b: StructureBlock | null) => void
  onType: (b: StructureBlock, type: string) => void
  onMove: (b: StructureBlock, delta: number) => void
  onArtifact: (b: StructureBlock) => void
}) {
  if (!tagged) return <p className="modal__hint">Add tags first (Check tab); then the reading order can be reviewed here.</p>
  return (
    <>
      <p className="modal__hint">This is the order a screen reader follows. Click a block to see it on the page.</p>
      <ol className="access__order">
        {blocks.map((b, i) => (
          <li key={b.id} data-selected={selected === b.id || undefined} style={{ paddingLeft: 4 + b.depth * 10 }}
              onClick={() => onSelect(b)}>
            <span className="access__num">{i + 1}</span>
            <select value={b.type} onClick={(e) => e.stopPropagation()} onChange={(e) => onType(b, e.target.value)}
                    disabled={b.type === 'LI' || b.type === 'L'}>
              {[b.type, 'P', 'H1', 'H2', 'H3', 'H4', 'Figure', 'Caption', 'BlockQuote', 'Note']
                .filter((t, j, all) => all.indexOf(t) === j).map((t) => <option key={t}>{t}</option>)}
            </select>
            <span className="access__text">{b.type === 'Figure' ? (b.alt ? `“${b.alt}”` : '(no alt text)') : b.text || '—'}</span>
            <span className="access__acts">
              <button className="iconbtn" title="Earlier" onClick={(e) => { e.stopPropagation(); onMove(b, -1) }}>{'↑'}</button>
              <button className="iconbtn" title="Later" onClick={(e) => { e.stopPropagation(); onMove(b, 1) }}>{'↓'}</button>
              <button className="iconbtn" title="Decoration: screen readers skip it"
                      onClick={(e) => { e.stopPropagation(); onArtifact(b) }}>{'⊘'}</button>
            </span>
          </li>
        ))}
      </ol>
    </>
  )
}

function AltText({ doc, blocks, onSave }: {
  doc: PDFDocumentProxy
  blocks: StructureBlock[]
  onSave: (b: StructureBlock, alt: string) => void
}) {
  if (!blocks.length) return <p className="modal__hint">No figures in the structure.</p>
  return (
    <>
      <p className="modal__hint">
        Describe what each image shows, as you would to someone on the phone. A purely
        decorative image should be marked as decoration in Reading order instead.
      </p>
      {blocks.map((b) => <AltItem key={b.id} doc={doc} block={b} onSave={onSave} />)}
    </>
  )
}

function AltItem({ doc, block, onSave }: { doc: PDFDocumentProxy; block: StructureBlock; onSave: (b: StructureBlock, alt: string) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const [alt, setAlt] = useState(block.alt ?? '')
  useEffect(() => setAlt(block.alt ?? ''), [block.alt])
  useEffect(() => {
    if (block.pageIndex === null || !block.box || !canvas.current) return
    let cancelled = false
    void doc.getPage(block.pageIndex + 1).then(async (page) => {
      const [x0, y0, x1, y1] = block.box!
      const scale = Math.min(2, 240 / Math.max(x1 - x0, 1))
      const viewport = page.getViewport({ scale })
      const full = document.createElement('canvas')
      full.width = viewport.width
      full.height = viewport.height
      await page.render({ canvasContext: full.getContext('2d')!, viewport }).promise
      if (cancelled || !canvas.current) return
      const [vx0, vy0, vx1, vy1] = viewport.convertToViewportRectangle([x0, y0, x1, y1])
      const w = Math.abs(vx1 - vx0)
      const h = Math.abs(vy1 - vy0)
      canvas.current.width = w
      canvas.current.height = h
      canvas.current.getContext('2d')!.drawImage(full, Math.min(vx0, vx1), Math.min(vy0, vy1), w, h, 0, 0, w, h)
    })
    return () => { cancelled = true }
  }, [doc, block])
  return (
    <div className="access__alt">
      <canvas ref={canvas} className="access__thumb" />
      <textarea rows={2} value={alt} placeholder="What this image shows"
                onChange={(e) => setAlt(e.target.value)} />
      <button className="btn btn--small" disabled={alt === (block.alt ?? '')} onClick={() => onSave(block, alt)}>Save</button>
    </div>
  )
}

function FieldTips({ fields, onSave }: { fields: FormField[]; onSave: (values: Record<string, string>) => void }) {
  const [values, setValues] = useState<Record<string, string>>({})
  useEffect(() => {
    setValues(Object.fromEntries(fields.map((f) => [f.name, f.tooltip || humanise(f.name)])))
  }, [fields])
  if (!fields.length) return <p className="modal__hint">This document has no form fields.</p>
  const changed = fields.filter((f) => (values[f.name] ?? '') !== f.tooltip)
  return (
    <>
      <p className="modal__hint">What a screen reader announces for each field. Suggestions come from the field names.</p>
      {fields.map((f) => (
        <label className="field" key={f.name}>
          <span>{f.name}{f.tooltip ? '' : ' — no description yet'}</span>
          <input value={values[f.name] ?? ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} />
        </label>
      ))}
      <button className="btn btn--primary btn--small" disabled={!changed.length}
              onClick={() => onSave(Object.fromEntries(changed.map((f) => [f.name, values[f.name] ?? ''])))}>
        Save {changed.length || ''} description{changed.length === 1 ? '' : 's'}
      </button>
    </>
  )
}
